import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useKruimelNamen } from '@/stores/kruimelNamenStore';
import { useAuthStore } from '@/stores/authStore';
import { useEffectiveAccess } from '@/lib/effectiveAccess';
import { werkkwaliteitStart } from '@/features/work-quality/tabs';
import { stepLabelKey } from '@/features/processes/format';
import { CANONICAL_PROCESS_NAMES } from '@/features/processes/processes.constants';

interface BreadcrumbItem {
  labelKey?: string;
  label?: string;
  /** Reservetekst als de vertaalsleutel ontbreekt (een stapcode zonder vertaling). */
  labelDefault?: string;
  to?: string;
}

export function getBreadcrumbItems(pathname: string): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean);
  const [first, second, third, fourth] = segments;

  if (first === 'data-quality') {
    const items: BreadcrumbItem[] = [{ labelKey: 'nav.dataQuality', to: '/data-quality' }];
    if (second) {
      items.push({ label: second, to: `/data-quality/${second}` });
    }
    if (third === 'products') items.push({ labelKey: 'nav.products', to: `/data-quality/${second}/products` });
    if (third === 'reports') {
      items.push({ labelKey: 'nav.reports' });
      const reportLabels: Record<string, string> = {
        activities: 'nav.activities',
        savings: 'nav.savings',
        compliance: 'nav.compliance',
        'management-summary': 'nav.managementSummary',
      };
      if (fourth) items.push({ labelKey: reportLabels[fourth], label: reportLabels[fourth] ? undefined : fourth, to: pathname });
    } else if (fourth) {
      items.push({ label: fourth, to: pathname });
    }
    return items;
  }

  // Story 4.1 — de route-stam van Werkkwaliteit. Zonder eigen regel toont het kruimelpad hier het
  // ruwe adres ("werkkwaliteit / medewerkers"), in beide talen gelijk. Het persoonsnummer is een
  // dynamische waarde; de medewerkerpagina zet de naam erbij in useKruimelNamen (story 11).
  if (first === 'werkkwaliteit') {
    const items: BreadcrumbItem[] = [{ labelKey: 'nav.werkkwaliteit', to: '/werkkwaliteit' }];
    const tabbladen: Record<string, string> = {
      medewerkers: 'qa.bediening.tabbalk.team',
      nakijkers: 'qa.bediening.tabbalk.nakijkers',
      ai: 'qa.bediening.tabbalk.ai',
      instellingen: 'qa.bediening.tabbalk.settings',
    };
    if (second && Object.hasOwn(tabbladen, second)) {
      items.push({ labelKey: tabbladen[second], to: `/werkkwaliteit/${second}` });
      if (second === 'medewerkers' && third) items.push({ label: third, to: pathname });
    } else if (second) {
      items.push({ label: second, to: pathname });
    }
    return items;
  }

  // spec-stappenlijst-weergave CAP-1: Processen, de dienst en de stap bij naam. Het padstuk
  // `steps` heeft geen eigen scherm en krijgt geen kruimel; niet-canonieke stukken blijven ruw.
  if (first === 'processes') {
    const items: BreadcrumbItem[] = [{ labelKey: 'nav.processes', to: '/processes' }];
    if (second) {
      const canoniek = (CANONICAL_PROCESS_NAMES as readonly string[]).includes(second);
      items.push(canoniek ? { labelKey: `processes.names.${second}`, to: `/processes/${second}` } : { label: second, to: `/processes/${second}` });
    }
    if (third === 'steps' && fourth) {
      let stap = fourth;
      try {
        stap = decodeURIComponent(fourth);
      } catch {
        // Onleesbare codering: de ruwe tekst blijft staan.
      }
      items.push({ labelKey: stepLabelKey(stap), labelDefault: stap, to: pathname });
    }
    return items;
  }

  if (first === 'admin') {
    return [
      { labelKey: 'nav.management', to: '/admin' },
      { labelKey: second === 'settings' ? 'nav.settings' : 'nav.mappingProfiles', to: pathname },
    ];
  }

  const labels: Record<string, string> = {
    dashboard: 'nav.dashboard',
    products: 'nav.products',
    intake: 'nav.intake',
    reports: 'nav.reports',
    activities: 'nav.activities',
    savings: 'nav.savings',
    compliance: 'nav.compliance',
    'management-summary': 'nav.managementSummary',
    quarterly: 'nav.quarterlyReport',
    orders: 'nav.orders',
    clients: 'nav.clients',
    users: 'nav.users',
    regelinzicht: 'nav.regelinzicht',
    behandeltijd: 'nav.handlingTime',
  };

  return segments.map((segment, index) => ({
    labelKey: labels[segment],
    label: labels[segment] ? undefined : segment,
    to: `/${segments.slice(0, index + 1).join('/')}`,
  }));
}

export function Breadcrumb() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const namen = useKruimelNamen((stand) => stand.namen);
  // CAP-6: voor de rol employee heet het menu-item "Feedbackrapporten"; het kruimelpad noemt dezelfde naam.
  const isFeedbackOnly = useAuthStore((stand) => stand.user?.role === 'employee');
  // CAP-10: de kruimel Werkkwaliteit wijst waar het menu-item heen gaat — Team voor wie het team mag zien.
  const { permissions } = useEffectiveAccess();
  const werkkwaliteitThuis = isFeedbackOnly ? '/werkkwaliteit' : werkkwaliteitStart(permissions);
  // Een ruwe sleutel in het adres zegt een bezoeker niets. Kent een scherm de naam erbij, dan
  // staat die hier; zo niet, dan blijft de sleutel staan — beter iets dan niets.
  // spec-tabelfilters story 12: de Processen-filter leeft in de gedeelde lijststand, dus geen adresparameters.
  const items = getBreadcrumbItems(pathname)
    .map((item) => (item.label && Object.hasOwn(namen, item.label) && namen[item.label] ? { ...item, label: namen[item.label] } : item))
    .map((item) => (isFeedbackOnly && item.labelKey === 'nav.werkkwaliteit' ? { ...item, labelKey: 'nav.myFeedback' } : item))
    .map((item) => (item.labelKey === 'nav.werkkwaliteit' ? { ...item, to: werkkwaliteitThuis } : item));

  if (items.length === 0) return null;

  return (
    <nav data-testid="breadcrumb" aria-label={t('breadcrumbs.label')} className="mb-4 text-sm text-[var(--foreground-muted)]">
      {/* Loopt terug op een smal scherm; een lang stuk breekt af in plaats van de pagina breder te maken. */}
      <ol className="flex flex-wrap items-center gap-1">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const label = item.labelKey ? t(item.labelKey, item.labelDefault != null ? { defaultValue: item.labelDefault } : undefined) : item.label;
          return (
          <li key={`${item.to ?? 'hierarchy'}-${item.labelKey ?? item.label}-${index}`} className="flex min-w-0 items-center gap-1 wrap-anywhere">
              {index > 0 && <span aria-hidden="true">/</span>}
              {isLast ? (
                <span aria-current="page" className="font-medium text-[var(--foreground)]">{label}</span>
              ) : item.to ? (
                <Link to={item.to} className="text-[var(--secondary)] hover:underline">{label}</Link>
              ) : (
                <span>{label}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
